import { Box } from '@mui/material';
import { ArrowLeft, X } from '@phosphor-icons/react';
import { Text } from '@telegram-apps/telegram-ui';
import { motion } from 'framer-motion';
import React from 'react';
import {
    LIQUID_GLASS_CIRCLE_PRESS_SCALE,
    LIQUID_GLASS_PRESS_ORIGIN,
    LIQUID_GLASS_PRESS_SCALE,
    MINI_BUTTON_ACTION_SIZE,
    liquidGlassCircleShadowSx,
    liquidGlassPillShadowSx,
    liquidGlassPressTransition,
    liquidGlassSurfaceFillSx,
} from '../helpers/liquidGlassSurface';
import { getButtonThemeColors } from '../helpers/theme';
import { useLiquidGlassPress } from '../helpers/useLiquidGlassPress';

function secondaryActionKind(text: string): 'back' | 'close' | 'label' {
    const normalized = text.trim().toLowerCase();
    if (normalized === 'close') return 'close';
    if (normalized === 'back' || normalized === 'cancel') return 'back';
    return 'label';
}

export interface SecondaryActionButtonProps {
    text: string;
    onClick: () => void;
    enabled?: boolean;
    navigation?: 'back' | 'close';
}

export const SecondaryActionButton: React.FC<SecondaryActionButtonProps> = ({ text, onClick, enabled = true, navigation }) => {
    const label = text || 'Back';
    const kind = navigation ?? secondaryActionKind(label);
    const disabled = !enabled;
    const disabledColors = getButtonThemeColors(false);
    const { pressed, onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onClick: handleClick } = useLiquidGlassPress({
        disabled,
        onActivate: onClick,
    });

    if (kind === 'label') {
        return (
            <Box
                component={motion.div}
                initial={false}
                animate={{ scale: pressed ? LIQUID_GLASS_PRESS_SCALE : 1 }}
                transition={liquidGlassPressTransition}
                sx={{
                    flexShrink: 0,
                    height: MINI_BUTTON_ACTION_SIZE,
                    transformOrigin: LIQUID_GLASS_PRESS_ORIGIN,
                }}
            >
                <Box
                    sx={{
                        height: MINI_BUTTON_ACTION_SIZE,
                        width: 'fit-content',
                        borderRadius: '999px',
                        overflow: 'visible',
                        ...liquidGlassPillShadowSx,
                    }}
                >
                    <Box
                        component="button"
                        type="button"
                        aria-label={label}
                        aria-disabled={disabled || undefined}
                        onClick={handleClick}
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={onPointerUp}
                        onPointerCancel={onPointerCancel}
                        sx={{
                            height: '100%',
                            p: 0,
                            m: 0,
                            px: 2.5,
                            border: 'none',
                            borderRadius: 'inherit',
                            boxSizing: 'border-box',
                            cursor: disabled ? 'default' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: disabled ? disabledColors.textColor : 'var(--tg-theme-text-color)',
                            WebkitTapHighlightColor: 'transparent',
                            outline: 'none',
                            whiteSpace: 'nowrap',
                            appearance: 'none',
                            WebkitAppearance: 'none',
                            ...liquidGlassSurfaceFillSx,
                            '&:focus-visible': {
                                outline: '2px solid var(--tgui--link_color)',
                                outlineOffset: 2,
                            },
                        }}
                    >
                        <Text weight="2" style={{ color: disabled ? disabledColors.textColor : 'var(--tg-theme-text-color)', fontSize: 17, lineHeight: 1 }}>
                            {label}
                        </Text>
                    </Box>
                </Box>
            </Box>
        );
    }

    return (
        <Box
            component={motion.div}
            initial={false}
            animate={{ scale: pressed ? LIQUID_GLASS_CIRCLE_PRESS_SCALE : 1 }}
            transition={liquidGlassPressTransition}
            sx={{
                flexShrink: 0,
                width: MINI_BUTTON_ACTION_SIZE,
                transformOrigin: LIQUID_GLASS_PRESS_ORIGIN,
            }}
        >
            <Box
                sx={{
                    width: MINI_BUTTON_ACTION_SIZE,
                    height: MINI_BUTTON_ACTION_SIZE,
                    borderRadius: '50%',
                    overflow: 'visible',
                    ...liquidGlassCircleShadowSx,
                }}
            >
                <Box
                    component="button"
                    type="button"
                    aria-label={label}
                    aria-disabled={disabled || undefined}
                    onClick={handleClick}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    onPointerCancel={onPointerCancel}
                    sx={{
                        width: '100%',
                        height: '100%',
                        p: 0,
                        m: 0,
                        borderRadius: 'inherit',
                        border: 'none',
                        boxSizing: 'border-box',
                        cursor: disabled ? 'default' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: disabled ? disabledColors.textColor : 'var(--tg-theme-text-color)',
                        WebkitTapHighlightColor: 'transparent',
                        outline: 'none',
                        appearance: 'none',
                        WebkitAppearance: 'none',
                        ...liquidGlassSurfaceFillSx,
                        '&:focus-visible': {
                            outline: '2px solid var(--tgui--link_color)',
                            outlineOffset: 2,
                        },
                    }}
                >
                    {kind === 'close' ? <X size={24} weight="bold" /> : <ArrowLeft size={24} weight="bold" />}
                </Box>
            </Box>
        </Box>
    );
};
